import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton, Checkbox, FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete, Search, Link as LinkIcon, Close, AttachMoney } from '@mui/icons-material';
import { assignmentsAPI, providersAPI, contractorsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';

const empty = { provider: '', contractor: '', payRate: '', payType: 'percentage', startDate: '', endDate: '' };

const Assignments = () => {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const uniqById = useCallback((list) => {
    const seen = new Set();
    const out = [];
    for (const item of (Array.isArray(list) ? list : [])) {
      const id = item?._id ?? item?.id;
      if (!id) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, []);

  const sortProvidersByName = useCallback((list) => {
    return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const an = (fullName(a) || '').toLowerCase();
      const bn = (fullName(b) || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, []);

  const loadProviders = useCallback(async (includeInactive) => {
    setProvidersLoading(true);
    try {
      const params = { limit: 10000, includeDetails: false };
      if (!includeInactive) params.isActive = true;
      const p = await providersAPI.getAll(params);
      const list = uniqById(Array.isArray(p.data) ? p.data : p.data?.data || []);
      setProviders(sortProvidersByName(list));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  }, [sortProvidersByName, uniqById]);

  const providerOptions = useMemo(() => {
    const base = includeInactiveProviders ? providers : providers.filter(p => p.isActive !== false);
    if (includeInactiveProviders) return base;
    if (!form.provider) return base;
    const selected = providers.find(p => String(p._id ?? p.id) === String(form.provider));
    if (selected && selected.isActive === false && !base.some(p => String(p._id ?? p.id) === String(form.provider))) {
      return [selected, ...base];
    }
    return base;
  }, [providers, includeInactiveProviders, form.provider]);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([assignmentsAPI.getAll(), contractorsAPI.getAll({ limit: 1000 })]);
      setRows(Array.isArray(a.data) ? a.data : a.data?.data || []);
      setContractors(Array.isArray(c.data) ? c.data : c.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Assignments | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviders(includeInactiveProviders); }, [includeInactiveProviders, loadProviders]);

  const filtered = rows.filter(r => {
    const pn = fullName(r.provider) || ''; const cn = fullName(r.contractor) || '';
    return [pn, cn].some(v => v.toLowerCase().includes(search.toLowerCase()));
  });

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      setForm({
        provider: row.provider?._id || '', contractor: row.contractor?._id || '',
        payRate: row.payRate || '', payType: row.payType || 'percentage',
        startDate: row.startDate ? row.startDate.slice(0, 10) : '',
        endDate: row.endDate ? row.endDate.slice(0, 10) : ''
      });
    } else { setEditing(null); setForm(empty); }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) await assignmentsAPI.update(editing, form);
      else await assignmentsAPI.create(form);
      setOpen(false); load();
      toast.success(editing ? 'Assignment updated successfully' : 'Assignment created successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async () => {
    try { await assignmentsAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Assignment deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const F = (f, v) => setForm(p => ({ ...p, [f]: v }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Assignments</Typography>
          <Typography variant="body2" color="text.secondary">Provider-contractor assignment management</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
          sx={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', boxShadow: '0 4px 14px rgba(6,182,212,0.3)', px: 3, fontWeight: 700 }}>
          New Assignment
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <TextField size="small" placeholder="Search assignments..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Contractor</TableCell>
                      <TableCell>Pay Rate</TableCell>
                      <TableCell>Pay Type</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                          <LinkIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No assignments found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => (
                        <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#06b6d4', 0.03) } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}>
                                {(r.provider?.firstName || '?')[0]}
                              </Avatar>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{fullName(r.provider) || 'N/A'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }}>
                                {(r.contractor?.firstName || '?')[0]}
                              </Avatar>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{fullName(r.contractor) || 'N/A'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#059669' }}>{r.payRate || '—'}{r.payType === 'percentage' ? '%' : ''}</Typography></TableCell>
                          <TableCell><Chip label={r.payType || 'percentage'} size="small" sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#06b6d4', 0.1), color: '#0891b2', textTransform: 'capitalize' }} /></TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.78rem' }}>
                              {r.startDate ? new Date(r.startDate).toLocaleDateString() : '—'} → {r.endDate ? new Date(r.endDate).toLocaleDateString() : 'Present'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#06b6d4' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #06b6d4, #0891b2)', borderRadius: 2 }}>
              <LinkIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography sx={{ fontWeight: 700 }}>{editing ? 'Edit Assignment' : 'New Assignment'}</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8' }}>Provider *</Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={includeInactiveProviders} onChange={(e) => setIncludeInactiveProviders(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Include inactive</Typography>}
                />
              </Box>
              <TextField fullWidth size="small" select disabled={providersLoading} value={form.provider} onChange={e => F('provider', e.target.value)}>
                <MenuItem value="">{providersLoading ? 'Loading providers…' : 'Select provider'}</MenuItem>
                {providerOptions.map(p => <MenuItem key={p._id} value={p._id}>{fullName(p)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Contractor *</Typography>
              <TextField fullWidth size="small" select value={form.contractor} onChange={e => F('contractor', e.target.value)}>
                <MenuItem value="">Select contractor</MenuItem>
                {contractors.map(c => <MenuItem key={c._id} value={c._id}>{fullName(c)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Pay Rate</Typography>
              <TextField fullWidth size="small" type="number" placeholder="10" value={form.payRate} onChange={e => F('payRate', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#94a3b8' }}>{form.payType === 'percentage' ? '%' : '$'}</Typography></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Pay Type</Typography>
              <TextField fullWidth size="small" select value={form.payType} onChange={e => F('payType', e.target.value)}>
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed Amount</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Start Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.startDate} onChange={e => F('startDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>End Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.endDate} onChange={e => F('endDate', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', fontWeight: 700, px: 4 }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Assignment?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Assignments;
